import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MailWarning, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

// Shown on authenticated pages while the user's email is not yet verified.
// Lets the user re-send the Firebase verification email and re-check status.
export const EmailVerificationBanner = () => {
  const { t } = useTranslation();
  const { user, emailVerified, sendVerification, reloadUser } = useAuth();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);

  if (!user || emailVerified) return null;

  const resend = async () => {
    setSending(true);
    try {
      await sendVerification();
      toast.success(t("verifyEmail.sent"));
    } catch (e) {
      toast.error(e?.code === "auth/too-many-requests" ? t("verifyEmail.alreadySent") : t("verifyEmail.sendFailed"));
    } finally {
      setSending(false);
    }
  };

  const recheck = async () => {
    setChecking(true);
    try {
      const verified = await reloadUser();
      if (verified) toast.success(t("verifyEmail.verified"));
      else toast.info(t("verifyEmail.stillPending"));
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="border-b border-amber-200 bg-amber-50" data-testid="email-verification-banner">
      <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
        <div className="flex items-start gap-2 text-sm text-amber-900">
          <MailWarning className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <span>{t("verifyEmail.message")}</span>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" variant="outline" className="h-8 rounded-full border-amber-300 text-amber-900 hover:bg-amber-100" onClick={resend} disabled={sending} data-testid="resend-verification-button">
            {sending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            {t("verifyEmail.resend")}
          </Button>
          <Button size="sm" className="h-8 rounded-full bg-amber-600 text-white hover:bg-amber-700" onClick={recheck} disabled={checking} data-testid="recheck-verification-button">
            {checking ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            {t("verifyEmail.iVerified")}
          </Button>
        </div>
      </div>
    </div>
  );
};
